import QRCode from 'qrcode'
import { GqlCustomField } from '../queries/sale_create'

export type CUSTOM_FIELD_ENTITY_TYPES = 'sale' | 'line_item'

export type CUSTOM_FIELD_ENTITY_GRAPHQL_TYPES = Uppercase<CUSTOM_FIELD_ENTITY_TYPES>

export type CUSTOM_FIELD_VALUE_TYPES =
  | 'string'
  | 'integer'
  | 'boolean'
  | 'date'
  | 'product_id'
  | 'qrcode'
  | 'string_array'
  | 'link'

export interface CustomFieldDefinition {
  id: string
  entity: CUSTOM_FIELD_ENTITY_TYPES
  name: string
  title: string
  type: CUSTOM_FIELD_VALUE_TYPES
}

export interface CustomFieldValue {
  /**
   * The name of the custom field.
   */
  name: string

  /**
   * The title of the custom field.
   */
  title: string

  /**
   * If the field should be visible in the UI.
   */
  visibleInUI: boolean

  /**
   * If the field should be printed on the recipt.
   */
  printOnReceipt: boolean

  /**
   * The value of the custom field.
   */
  value: string | number | boolean | string[] | null

  /**
   * In case of a QR Code, this is a URI of the image.
   * Only used for rendering receipts with QR Codes.
   */
  imageURI?: string

  /**
   * The ID if the definition of the custom field.
   */
  definitionId: string

  /**
   * The type of value of the custom field.
   */
  type: CUSTOM_FIELD_VALUE_TYPES
}

export interface CustomFieldMap {
  [id: string]: {
    entity: CUSTOM_FIELD_ENTITY_GRAPHQL_TYPES
    values: GqlCustomField[]
  }
}

/**
 * Get image uri for a customer field value
 */
export async function getImageURI(value: CustomFieldValue) {
  let imageURI = value.imageURI
  if (value.type === 'qrcode' && value.value) {
    try {
      imageURI = await QRCode.toDataURL(value.value.toString(), {
        scale: 3,
      })
    } catch (err) {}
  }
  return imageURI
}

/**
 * Add or update a custom field to the entity with the value provided
 * @param {CustomFieldValue} item The custom field value you want to add / update.
 */
export function setCustomField(
  item: CustomFieldValue,
  customFields: CustomFieldValue[]
): CustomFieldValue[] {
  let itemUpdated = false
  const updatedCustomFields = customFields.map(customField => {
    if (customField.name === item.name) {
      /**
       * sale.customFields is being migrated to redux
       * so all customField items immutable, so cannot mutate customField item directly
       * customFields array still mutable so can assign it to new array or push item into it
       * Note: lineItem is not migrated to redux yet, so its customFields array and all its customField items are still mutable
       * The mutation below is done on a copied object, so it is safe to use both on mutable object & immutable object
       */
      const updatedCustomField = {
        ...customField,
        definitionId: item.definitionId,
        type: item.type,
        value: item.value,
      }
      itemUpdated = true
      return updatedCustomField
    }
    return customField
  })

  if (!itemUpdated) {
    updatedCustomFields.push(item)
  }

  return updatedCustomFields
}

/**
 * Looks up the value of a custom field.
 *
 * @param {string} name The name of the custom field that you want to get.
 *
 * @returns {CustomFieldValue | undefined} The custom field matched the name provided
 */
export function getCustomField(name: string, customFields: CustomFieldValue[]) {
  const entry = customFields.find(entry => entry.name === name)
  return entry
}
